import React, { useEffect, useRef, useState } from "react";
import Portal from "@mui/material/Portal";
import Fade from "@mui/material/Fade";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";

const SNACKBAR_WIDTH = 360;
const VIEWPORT_MARGIN = 16;
const DEFAULT_HEIGHT = 120;

function clampPosition(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getDefaultPosition(height = DEFAULT_HEIGHT) {
  return {
    x: window.innerWidth - SNACKBAR_WIDTH - VIEWPORT_MARGIN,
    y: window.innerHeight - height - VIEWPORT_MARGIN,
  };
}

export default function AnnotationSaveSnackbar({
  open,
  message = "Saving annotations...",
}) {
  const paperRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const [position, setPosition] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!open) return;

    const height = paperRef.current?.offsetHeight || DEFAULT_HEIGHT;
    setPosition((prev) => prev ?? getDefaultPosition(height));
  }, [open]);

  useEffect(() => {
    if (!isDragging) return undefined;

    const handleMouseMove = (event) => {
      const width = paperRef.current?.offsetWidth || SNACKBAR_WIDTH;
      const height = paperRef.current?.offsetHeight || DEFAULT_HEIGHT;
      const maxX = window.innerWidth - width - VIEWPORT_MARGIN;
      const maxY = window.innerHeight - height - VIEWPORT_MARGIN;

      setPosition({
        x: clampPosition(
          event.clientX - dragOffsetRef.current.x,
          VIEWPORT_MARGIN,
          Math.max(VIEWPORT_MARGIN, maxX),
        ),
        y: clampPosition(
          event.clientY - dragOffsetRef.current.y,
          VIEWPORT_MARGIN,
          Math.max(VIEWPORT_MARGIN, maxY),
        ),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const handleMouseDown = (event) => {
    if (event.button !== 0 || !paperRef.current) return;

    const rect = paperRef.current.getBoundingClientRect();
    dragOffsetRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    setPosition({ x: rect.left, y: rect.top });
    setIsDragging(true);
    event.preventDefault();
  };

  return (
    <Portal>
      <Fade in={open} unmountOnExit>
        <Paper
          ref={paperRef}
          elevation={6}
          onMouseDown={handleMouseDown}
          sx={{
            position: "fixed",
            left: position?.x ?? getDefaultPosition().x,
            top: position?.y ?? getDefaultPosition().y,
            zIndex: (theme) => theme.zIndex.snackbar,
            width: SNACKBAR_WIDTH,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid #90caf9",
            bgcolor: "#e3f2fd",
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: "none",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
            <CircularProgress size={22} thickness={5} sx={{ mt: 0.25 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: "#1565c0", lineHeight: 1.3 }}
              >
                Annotation save in progress
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#0d47a1", mt: 0.25, lineHeight: 1.35 }}
              >
                {message}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#546e7a", display: "block", mt: 0.75 }}
              >
                Don&apos;t close this tab, but you can continue with your work.
              </Typography>
              <Box sx={{ mt: 1 }}>
                <LinearProgress
                  variant="indeterminate"
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: "#bbdefb",
                    "& .MuiLinearProgress-bar": { bgcolor: "#1976d2" },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Portal>
  );
}
